import { useState, useEffect, useCallback, useMemo } from "react";
import { aGet, aPost, aPut, aDelete } from "../../utils/adminApi";
import {
  C,
  Input,
  Select,
  Btn,
  Table,
  Modal,
  EmptyState,
  LoadingSpinner,
} from "./AdminPanel";

// React Icons imports
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiTag,
  FiPercent,
  FiDollarSign,
  FiCalendar,
  FiUsers,
  FiCheck,
  FiX,
  FiInbox,
  FiSearch,
  FiFilter,
  FiCopy,
  FiClock,
  FiAward,
  FiTrendingUp,
  FiAlertCircle,
} from "react-icons/fi";
import {
  FaRupeeSign,
  FaTicketAlt,
  FaGift,
  FaStopwatch,
  FaInfinity,
  FaToggleOn,
  FaToggleOff,
  FaChartLine,
  FaRegClock,
} from "react-icons/fa";
import {
  MdLocalOffer,
  MdDiscount,
  MdMoneyOff,
  MdTimer,
  MdPeople,
  MdCheckCircle,
  MdCancel,
  MdOutlineVerified,
  MdWarning,
} from "react-icons/md";

const emptyCoupon = {
  code: "",
  discountType: "percentage",
  discountValue: "",
  minOrderValue: 0,
  maxDiscount: "",
  usageLimit: "",
  isActive: true,
  expiresAt: "",
};

// Helper function to check if coupon is expired
const isCouponExpired = (expiresAt) => {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
};

// Coupon Card Component for Mobile View
function CouponCardMobile({ coupon, onEdit, onDelete, onToggleActive }) {
  const expired = isCouponExpired(coupon.expiresAt);
  const usagePercent = coupon.usageLimit
    ? (coupon.usedCount / coupon.usageLimit) * 100
    : 0;

  return (
    <div className="coupon-card-enhanced">
      <div className="coupon-card-header">
        <div className="coupon-code-badge">
          <FaTicketAlt />
          <span className="coupon-code">{coupon.code}</span>
        </div>
        <button
          className={`status-toggle ${coupon.isActive && !expired ? "active" : "inactive"}`}
          onClick={() => onToggleActive(coupon)}
        >
          {coupon.isActive && !expired ? <FiCheck /> : <FiX />}
          {coupon.isActive && !expired ? "Active" : "Inactive"}
        </button>
      </div>

      <div className="coupon-card-body">
        <div className="coupon-discount">
          {coupon.discountType === "percentage" ? (
            <span className="discount-value">
              <FiPercent /> {coupon.discountValue}% OFF
            </span>
          ) : (
            <span className="discount-value">
              <FaRupeeSign /> {coupon.discountValue} OFF
            </span>
          )}
        </div>

        <div className="coupon-details">
          <div className="detail-item">
            <FaRupeeSign size={12} />
            <span>Min: ₹{coupon.minOrderValue}</span>
          </div>
          {coupon.maxDiscount && (
            <div className="detail-item">
              <MdMoneyOff size={12} />
              <span>Max: ₹{coupon.maxDiscount}</span>
            </div>
          )}
          <div className="detail-item">
            <FiUsers size={12} />
            <span>
              Used: {coupon.usedCount || 0}/{coupon.usageLimit || "∞"}
            </span>
          </div>
          <div className="detail-item">
            {coupon.expiresAt ? (
              <>
                <FiClock size={12} />
                <span className={expired ? "expired" : ""}>
                  {expired
                    ? "Expired"
                    : new Date(coupon.expiresAt).toLocaleDateString("en-IN")}
                </span>
              </>
            ) : (
              <>
                <FaInfinity size={12} />
                <span>Never</span>
              </>
            )}
          </div>
        </div>

        {coupon.usageLimit && (
          <div className="usage-progress">
            <div
              className="progress-bar"
              style={{ width: `${Math.min(usagePercent, 100)}%` }}
            />
            <span className="progress-text">
              {Math.round(usagePercent)}% used
            </span>
          </div>
        )}
      </div>

      <div className="coupon-card-actions">
        <button onClick={() => onEdit(coupon)} className="action-btn edit">
          <FiEdit2 /> Edit
        </button>
        <button
          onClick={() => onDelete(coupon.id)}
          className="action-btn delete"
        >
          <FiTrash2 /> Delete
        </button>
      </div>
    </div>
  );
}

export default function Coupons({ toast }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyCoupon);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    aGet("/coupons")
      .then(setCoupons)
      .catch((e) => toast(e.message, "error"))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setForm({ ...emptyCoupon });
    setModal("add");
  };

  const openEdit = (c) => {
    setForm({
      ...c,
      expiresAt: c.expiresAt ? c.expiresAt.split("T")[0] : "",
    });
    setModal("edit");
  };

  const handleSave = async () => {
    if (!form.code || !form.discountValue) {
      toast("Code and discount value required", "error");
      return;
    }
    if (form.discountType === "percentage" && form.discountValue > 100) {
      toast("Percentage discount cannot exceed 100%", "error");
      return;
    }
    setSaving(true);
    try {
      if (modal === "add") await aPost("/coupons", form);
      else await aPut(`/coupons/${form.id}`, form);
      toast(
        modal === "add"
          ? "Coupon created successfully ✓"
          : "Coupon updated successfully ✓",
      );
      setModal(null);
      load();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;
    try {
      await aDelete(`/coupons/${id}`);
      toast("Coupon deleted successfully");
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const toggleActive = async (c) => {
    try {
      await aPut(`/coupons/${c.id}`, { ...c, isActive: !c.isActive });
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const copyCouponCode = (code) => {
    navigator.clipboard.writeText(code);
    toast("Coupon code copied to clipboard!");
  };

  // Filter coupons
  const filteredCoupons = useMemo(() => {
    let filtered = [...coupons];

    // Search filter
    if (search) {
      filtered = filtered.filter((c) =>
        c.code.toLowerCase().includes(search.toLowerCase()),
      );
    }

    // Status filter
    if (statusFilter === "active") {
      filtered = filtered.filter(
        (c) => c.isActive && !isCouponExpired(c.expiresAt),
      );
    } else if (statusFilter === "inactive") {
      filtered = filtered.filter((c) => !c.isActive);
    } else if (statusFilter === "expired") {
      filtered = filtered.filter((c) => isCouponExpired(c.expiresAt));
    }

    // Type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter((c) => c.discountType === typeFilter);
    }

    return filtered;
  }, [coupons, search, statusFilter, typeFilter]);

  const stats = useMemo(() => {
    const total = coupons.length;
    const active = coupons.filter(
      (c) => c.isActive && !isCouponExpired(c.expiresAt),
    ).length;
    const expired = coupons.filter((c) => isCouponExpired(c.expiresAt)).length;
    const totalUsage = coupons.reduce((sum, c) => sum + (c.usedCount || 0), 0);
    return { total, active, expired, totalUsage };
  }, [coupons]);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setTypeFilter("all");
  };

  return (
    <div className="coupons-container-enhanced">
      {/* Header Section */}
      <div className="coupons-header-enhanced">
        {/* <div className="header-title-section">
          <div className="header-icon">
            <MdLocalOffer />
          </div>
          <div>
            <h1 className="header-title">Coupon Manager</h1>
            <p className="header-subtitle">
              Create and manage discount coupons for your customers
            </p>
          </div>
        </div> */}
        <div className="header-stats">
          <div className="stat-card-mini">
            <FaTicketAlt />
            <div>
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total</span>
            </div>
          </div>
          <div className="stat-card-mini active">
            <MdCheckCircle />
            <div>
              <span className="stat-value">{stats.active}</span>
              <span className="stat-label">Active</span>
            </div>
          </div>
          <div className="stat-card-mini expired">
            <MdWarning />
            <div>
              <span className="stat-value">{stats.expired}</span>
              <span className="stat-label">Expired</span>
            </div>
          </div>
          <div className="stat-card-mini usage">
            <FiTrendingUp />
            <div>
              <span className="stat-value">{stats.totalUsage}</span>
              <span className="stat-label">Uses</span>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="coupons-toolbar-enhanced">
        <div className="toolbar-left">
          <div className="search-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search coupons by code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
            {search && (
              <button onClick={() => setSearch("")} className="search-clear">
                <FiX />
              </button>
            )}
          </div>
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="expired">Expired</option>
          </select>
          <select
            className="filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed Amount</option>
          </select>
          {(search || statusFilter !== "all" || typeFilter !== "all") && (
            <button onClick={clearFilters} className="clear-filters-btn">
              <FiX /> Clear
            </button>
          )}
        </div>
        <button className="add-coupon-btn" onClick={openAdd}>
          <FiPlus /> Create Coupon
        </button>
      </div>

      {/* Result Count */}
      {!loading && (
        <div className="result-count">
          Showing {filteredCoupons.length} of {coupons.length} coupons
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="coupons-loading">
          <LoadingSpinner size="lg" text="Loading coupons..." />
        </div>
      )}

      {/* Desktop Table View */}
      {!loading && filteredCoupons.length > 0 && (
        <div className="coupons-table-wrapper">
          <Table
            loading={loading}
            columns={[
              {
                key: "code",
                label: "Code",
                render: (v, row) => (
                  <div className="coupon-code-cell">
                    <span className="code-badge">{v}</span>
                    <button
                      className="copy-code-btn"
                      onClick={() => copyCouponCode(v)}
                      title="Copy code"
                    >
                      <FiCopy size={12} />
                    </button>
                  </div>
                ),
              },
              {
                key: "discountType",
                label: "Type",
                render: (v) => (
                  <span className={`type-badge ${v}`}>
                    {v === "percentage" ? <FiPercent /> : <FaRupeeSign />}
                    {v === "percentage" ? "Percentage" : "Fixed"}
                  </span>
                ),
              },
              {
                key: "discountValue",
                label: "Value",
                render: (v, row) => (
                  <span className="discount-value-cell">
                    {row.discountType === "percentage"
                      ? `${v}% OFF`
                      : `₹${v} OFF`}
                  </span>
                ),
              },
              {
                key: "minOrderValue",
                label: "Min Order",
                render: (v) => (
                  <span className="min-order-cell">
                    <FaRupeeSign />{" "}
                    {v === 0 ? "No minimum" : v.toLocaleString()}
                  </span>
                ),
              },
              {
                key: "usageLimit",
                label: "Uses",
                render: (v, row) => {
                  const used = row.usedCount || 0;
                  const limit = v || "∞";
                  const percent = v ? (used / v) * 100 : 0;
                  return (
                    <div className="usage-cell">
                      <div className="usage-text">
                        {used}/{limit}
                      </div>
                      {v && (
                        <div className="usage-bar-wrapper">
                          <div
                            className="usage-bar"
                            style={{ width: `${Math.min(percent, 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  );
                },
              },
              {
                key: "expiresAt",
                label: "Expires",
                render: (v) => {
                  const expired = isCouponExpired(v);
                  return (
                    <span className={`expiry-cell ${expired ? "expired" : ""}`}>
                      {v ? (
                        <>
                          <FiCalendar />
                          {new Date(v).toLocaleDateString("en-IN")}
                          {expired && (
                            <span className="expired-badge">Expired</span>
                          )}
                        </>
                      ) : (
                        <>
                          <FaInfinity />
                          Never
                        </>
                      )}
                    </span>
                  );
                },
              },
              {
                key: "isActive",
                label: "Status",
                render: (v, row) => {
                  const expired = isCouponExpired(row.expiresAt);
                  const isActive = v && !expired;
                  return (
                    <button
                      className={`status-btn ${isActive ? "active" : "inactive"}`}
                      onClick={() => toggleActive(row)}
                    >
                      {isActive ? <FiCheck /> : <FiX />}
                      {isActive ? "Active" : expired ? "Expired" : "Inactive"}
                    </button>
                  );
                },
              },
              {
                key: "id",
                label: "Actions",
                render: (v, row) => (
                  <div className="action-buttons">
                    <button
                      className="action-icon edit"
                      onClick={() => openEdit(row)}
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="action-icon delete"
                      onClick={() => handleDelete(v)}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ),
              },
            ]}
            data={filteredCoupons}
          />
        </div>
      )}

      {/* Mobile Card View */}
      {!loading && filteredCoupons.length > 0 && (
        <div className="coupons-mobile-view">
          {filteredCoupons.map((coupon) => (
            <CouponCardMobile
              key={coupon.id}
              coupon={coupon}
              onEdit={openEdit}
              onDelete={handleDelete}
              onToggleActive={toggleActive}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredCoupons.length === 0 && (
        <EmptyState
          icon={MdLocalOffer}
          title="No coupons found"
          description={
            search || statusFilter !== "all" || typeFilter !== "all"
              ? "Try adjusting your search or filter criteria"
              : "Create your first coupon to start offering discounts"
          }
          action={
            search || statusFilter !== "all" || typeFilter !== "all"
              ? { label: "Clear filters", onClick: clearFilters }
              : { label: "Create Coupon", onClick: openAdd }
          }
        />
      )}

      {/* Add/Edit Modal */}
      {modal && (
        <Modal
          title={modal === "add" ? "Create New Coupon" : "Edit Coupon"}
          onClose={() => setModal(null)}
          size="md"
        >
          <div className="coupon-modal-enhanced">
            <div className="modal-grid">
              {/* Left Column */}
              <div className="modal-left">
                <div className="form-group">
                  <label className="form-label">Coupon Code *</label>
                  <div className="code-input-wrapper">
                    <MdLocalOffer className="input-icon" />
                    <input
                      className="form-input"
                      value={form.code}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          code: e.target.value.toUpperCase().replace(/\s/g, ""),
                        }))
                      }
                      placeholder="e.g., SAVE20"
                    />
                  </div>
                  <p className="input-hint">
                    Use uppercase letters and numbers only
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">Discount Type</label>
                  <div className="type-toggle">
                    <button
                      className={`type-option ${form.discountType === "percentage" ? "active" : ""}`}
                      onClick={() =>
                        setForm((p) => ({ ...p, discountType: "percentage" }))
                      }
                    >
                      <FiPercent /> Percentage (%)
                    </button>
                    <button
                      className={`type-option ${form.discountType === "fixed" ? "active" : ""}`}
                      onClick={() =>
                        setForm((p) => ({ ...p, discountType: "fixed" }))
                      }
                    >
                      <FaRupeeSign /> Fixed Amount
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Discount Value *
                    {form.discountType === "percentage" && (
                      <span className="label-hint">(max 100%)</span>
                    )}
                  </label>
                  <div className="code-input-wrapper">
                    {form.discountType === "percentage" ? (
                      <FiPercent className="input-icon" />
                    ) : (
                      <FaRupeeSign className="input-icon" />
                    )}
                    <input
                      type="number"
                      className="form-input"
                      value={form.discountValue}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          discountValue: Number(e.target.value),
                        }))
                      }
                      placeholder={
                        form.discountType === "percentage"
                          ? "e.g., 20"
                          : "e.g., 100"
                      }
                      min="0"
                      max={form.discountType === "percentage" ? 100 : undefined}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Minimum Order Value (₹)</label>
                  <div className="code-input-wrapper">
                    <FaRupeeSign className="input-icon" />
                    <input
                      type="number"
                      className="form-input"
                      value={form.minOrderValue}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          minOrderValue: Number(e.target.value),
                        }))
                      }
                      placeholder="0 for no minimum"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="modal-right">
                <div className="form-group">
                  <label className="form-label">Maximum Discount (₹)</label>
                  <div className="code-input-wrapper">
                    <MdMoneyOff className="input-icon" />
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Leave empty for no limit"
                      value={form.maxDiscount}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          maxDiscount: e.target.value
                            ? Number(e.target.value)
                            : "",
                        }))
                      }
                      min="0"
                    />
                  </div>
                  <p className="input-hint">
                    Maximum discount amount that can be applied
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">Usage Limit</label>
                  <div className="code-input-wrapper">
                    <FiUsers className="input-icon" />
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Leave empty for unlimited"
                      value={form.usageLimit}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          usageLimit: e.target.value
                            ? Number(e.target.value)
                            : "",
                        }))
                      }
                      min="1"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Expiration Date</label>
                  <div className="code-input-wrapper">
                    <FiCalendar className="input-icon" />
                    <input
                      type="date"
                      className="form-input"
                      value={form.expiresAt}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, expiresAt: e.target.value }))
                      }
                    />
                  </div>
                  <p className="input-hint">Leave empty for never expires</p>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, isActive: e.target.checked }))
                      }
                    />
                    <span className="checkbox-text">
                      <MdCheckCircle />
                      Active
                    </span>
                  </label>
                  <p className="input-hint">
                    Inactive coupons cannot be used by customers
                  </p>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <div className="spinner-small" /> : <FiCheck />}
                {modal === "add" ? "Create Coupon" : "Save Changes"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <style jsx>{`
        .coupons-container-enhanced {
          padding: 0 4px 32px;
          animation: fadeIn 0.35s ease;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Header */
        .coupons-header-enhanced {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 28px;
          flex-wrap: wrap;
          gap: 20px;
        }

        .header-title-section {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .header-icon {
          width: 52px;
          height: 52px;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          color: white;
        }

        .header-title {
          font-size: 26px;
          font-weight: 700;
          color: var(--text);
          letter-spacing: -0.02em;
          margin-bottom: 6px;
        }

        .header-subtitle {
          font-size: 14px;
          color: var(--text-muted);
        }

        .header-stats {
          display: flex;
          gap: 12px;
        }

        .stat-card-mini {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 16px;
          background: var(--surface);
          border-radius: 14px;
          border: 1px solid var(--border-light);
        }

        .stat-card-mini svg {
          font-size: 20px;
          color: var(--primary);
        }

        .stat-card-mini.active svg {
          color: #10b981;
        }
        .stat-card-mini.expired svg {
          color: #ef4444;
        }
        .stat-card-mini.usage svg {
          color: #8b5cf6;
        }

        .stat-card-mini div {
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          font-size: 18px;
          font-weight: 700;
          color: var(--text);
        }

        .stat-label {
          font-size: 10px;
          color: var(--text-muted);
        }

        /* Toolbar */
        .coupons-toolbar-enhanced {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 20px;
        }

        .toolbar-left {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .search-wrapper {
          position: relative;
          min-width: 260px;
        }

        .search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-muted);
        }

        .search-input {
          width: 100%;
          padding: 10px 36px 10px 42px;
          border: 1.5px solid var(--border-light);
          border-radius: 12px;
          font-size: 13px;
          background: var(--surface);
        }

        .search-input:focus {
          outline: none;
          border-color: var(--primary);
        }

        .search-clear {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-muted);
        }

        .filter-select {
          padding: 10px 36px 10px 14px;
          border: 1.5px solid var(--border-light);
          border-radius: 12px;
          font-size: 13px;
          background: var(--surface);
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
        }

        .clear-filters-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: transparent;
          border: 1px solid var(--border-light);
          border-radius: 10px;
          font-size: 12px;
          cursor: pointer;
        }

        .add-coupon-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 22px;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          border: none;
          border-radius: 12px;
          color: white;
          font-weight: 600;
          cursor: pointer;
        }

        .result-count {
          margin-bottom: 16px;
          font-size: 13px;
          color: var(--text-muted);
        }

        /* Table Styles */
        .coupons-table-wrapper {
          background: var(--surface);
          border-radius: 20px;
          border: 1px solid var(--border-light);
          overflow: hidden;
        }

        .coupon-code-cell {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .code-badge {
          font-family: monospace;
          font-weight: 800;
          font-size: 14px;
          color: #f59e0b;
          background: rgba(245, 158, 11, 0.1);
          padding: 4px 10px;
          border-radius: 8px;
          letter-spacing: 0.5px;
        }

        .copy-code-btn {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          border: 1px solid var(--border-light);
          background: transparent;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .copy-code-btn:hover {
          background: var(--surface-2);
          border-color: #f59e0b;
          color: #f59e0b;
        }

        .type-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }

        .type-badge.percentage {
          background: rgba(139, 92, 246, 0.1);
          color: #8b5cf6;
        }

        .type-badge.fixed {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .discount-value-cell {
          font-weight: 700;
          color: var(--text);
        }

        .min-order-cell {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 13px;
        }

        .usage-cell {
          min-width: 80px;
        }

        .usage-text {
          font-size: 12px;
          margin-bottom: 4px;
        }

        .usage-bar-wrapper {
          height: 4px;
          background: var(--border-light);
          border-radius: 4px;
          overflow: hidden;
        }

        .usage-bar {
          height: 100%;
          background: linear-gradient(90deg, #10b981, #34d399);
          border-radius: 4px;
        }

        .expiry-cell {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
        }

        .expiry-cell.expired {
          color: #ef4444;
        }

        .expired-badge {
          font-size: 10px;
          padding: 2px 6px;
          background: rgba(239, 68, 68, 0.1);
          border-radius: 20px;
          color: #ef4444;
        }

        .status-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          border: none;
          cursor: pointer;
        }

        .status-btn.active {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .status-btn.inactive {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .action-buttons {
          display: flex;
          gap: 6px;
        }

        .action-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1px solid var(--border-light);
          background: transparent;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .action-icon.edit:hover {
          background: rgba(245, 158, 11, 0.1);
          border-color: #f59e0b;
          color: #f59e0b;
        }

        .action-icon.delete:hover {
          background: rgba(239, 68, 68, 0.1);
          border-color: #ef4444;
          color: #ef4444;
        }

        /* Mobile Card View */
        .coupons-mobile-view {
          display: none;
          flex-direction: column;
          gap: 16px;
        }

        .coupon-card-enhanced {
          background: var(--surface);
          border: 1px solid var(--border-light);
          border-radius: 20px;
          padding: 16px;
        }

        .coupon-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--border-light);
        }

        .coupon-code-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: monospace;
          font-weight: 800;
          font-size: 16px;
          color: #f59e0b;
        }

        .status-toggle {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          border: none;
          cursor: pointer;
        }

        .status-toggle.active {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .status-toggle.inactive {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .coupon-card-body {
          margin-bottom: 14px;
        }

        .discount-value {
          font-size: 20px;
          font-weight: 700;
          color: var(--text);
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 12px;
        }

        .coupon-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
          margin-bottom: 12px;
        }

        .detail-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--text-muted);
        }

        .detail-item .expired {
          color: #ef4444;
        }

        .usage-progress {
          margin-top: 8px;
        }

        .progress-bar {
          height: 4px;
          background: linear-gradient(90deg, #10b981, #34d399);
          border-radius: 4px;
        }

        .progress-text {
          font-size: 10px;
          color: var(--text-muted);
          margin-top: 4px;
          display: inline-block;
        }

        .coupon-card-actions {
          display: flex;
          gap: 10px;
          padding-top: 12px;
          border-top: 1px solid var(--border-light);
        }

        .action-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          border: 1px solid var(--border-light);
          background: transparent;
        }

        .action-btn.edit:hover {
          background: rgba(245, 158, 11, 0.1);
          border-color: #f59e0b;
          color: #f59e0b;
        }

        .action-btn.delete:hover {
          background: rgba(239, 68, 68, 0.1);
          border-color: #ef4444;
          color: #ef4444;
        }

        /* Modal Styles */
        .coupon-modal-enhanced {
          padding: 8px 0;
        }

        .modal-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          margin-bottom: 24px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: var(--text-muted);
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .label-hint {
          font-size: 10px;
          font-weight: normal;
          color: #f59e0b;
          margin-left: 6px;
        }

        .code-input-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-muted);
          font-size: 16px;
        }

        .form-input {
          width: 100%;
          padding: 11px 14px 11px 40px;
          border: 1.5px solid var(--border-light);
          border-radius: 12px;
          font-size: 13px;
          background: var(--surface);
          color: black;
        }

        .form-input:focus {
          outline: none;
          border-color: #f59e0b;
        }

        .input-hint {
          font-size: 10px;
          color: var(--text-muted);
          margin-top: 6px;
        }

        .type-toggle {
          display: flex;
          gap: 10px;
        }

        .type-option {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px;
          border: 1.5px solid var(--border-light);
          border-radius: 10px;
          background: transparent;
          cursor: pointer;
          font-size: 13px;
          transition: all 0.2s;
        }

        .type-option.active {
          background: rgba(245, 158, 11, 0.1);
          border-color: #f59e0b;
          color: #f59e0b;
        }

        .checkbox-group {
          display: flex;
          flex-direction: column;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          margin-bottom: 6px;
        }

        .checkbox-text {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 500;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding-top: 20px;
          border-top: 1px solid var(--border-light);
        }

        .btn-cancel {
          padding: 10px 20px;
          background: transparent;
          border: 1.5px solid var(--border-light);
          border-radius: 10px;
          cursor: pointer;
          font-weight: 500;
        }

        .btn-save {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          border: none;
          border-radius: 10px;
          color: white;
          font-weight: 600;
          cursor: pointer;
        }

        .spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .coupons-loading {
          display: flex;
          justify-content: center;
          padding: 60px;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .coupons-table-wrapper {
            display: none;
          }

          .coupons-mobile-view {
            display: flex;
          }

          .toolbar-left {
            flex-direction: column;
            align-items: stretch;
            width: 100%;
          }

          .search-wrapper {
            width: 100%;
          }

          .filter-select {
            width: 100%;
          }

          .coupons-toolbar-enhanced {
            flex-direction: column;
          }

          .add-coupon-btn {
            width: 100%;
            justify-content: center;
          }

          .header-stats {
            width: 100%;
            justify-content: space-between;
          }

          .stat-card-mini {
            flex: 1;
            justify-content: center;
          }

          .modal-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }

        @media (max-width: 480px) {
          .header-title-section {
            width: 100%;
          }

          .header-stats {
            flex-wrap: wrap;
          }

          .stat-card-mini {
            min-width: calc(50% - 6px);
          }

          .coupon-details {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
